<!-- 
	all links to title detail page should be wrapped with this component.
	in certain countries we don't have title detail pages anymore, they should open the title detail modal.
-->

<template>
	<a
		v-if="isExternalLink()"
		v-bind="$attrs"
		:href="fullPath"
		target="_blank"
		rel="nofollow noopener"
		@click="$emit('clickOut')"
	>
		<slot />
	</a>

	<a v-else-if="!!fullPath" v-bind="$attrs" :href="fullPath" @click.exact="$event => navigate($event, fullPath)">
		<slot />
	</a>

	<!-- Using a button tag here can cause hydration issues if there is a button somewhere in the slot. -->
	<div v-else v-bind="$attrs" role="button" tabindex="0" class="poster-link" @click.prevent="() => onClick(title)">
		<slot />
	</div>
</template>

<script setup lang="ts">
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import type { ObjectType } from '@/@types/graphql-types';
import { openTitleDetailModal } from '@/helpers/title-modal-helper';

interface MinimumLinkTitle {
	objectId: number;
	objectType: ObjectType;
	content: {
		title: string;
		originalReleaseYear?: number | null;
	};
}

defineOptions({ inheritAttrs: false });

const router = useRouter();
const props = defineProps<{ title: MinimumLinkTitle; fullPath: string }>();

const emit = defineEmits(['clickOut', 'click']);

function isExternalLink() {
	return /^(http|https|www)/i.test(props.fullPath);
}

async function navigate(evt: MouseEvent, fullPath: string) {
	evt.preventDefault();
	emit('click');

	// INP case: artificially give the JS thread time to breathe by adding a re-render.
	await new Promise(resolve => setTimeout(resolve, 0));

	router.push(fullPath).catch((error: any) => {
		if (
			!isNavigationFailure(error, NavigationFailureType.duplicated) &&
			!isNavigationFailure(error, NavigationFailureType.cancelled)
		) {
			throw error;
		}
	});
}

// Open up modal if title doesn't have a fullPath.
// That means it exists but doesn't have an URL in this country.
//
// DO NOT USE `async` on this function.
// Used together with dynamic imports, it will stop vue's (click) event bubbling.
function onClick(title: MinimumLinkTitle) {
	emit('click');
	openTitleDetailModal(title, 'TitleDetailLink');
}
</script>

<style lang="scss">
.poster-link {
	all: unset;
	width: 100%;
	display: block;
	cursor: pointer;
}
</style>
